import React from 'react';
import type { ClassReportType, HighlightedProps, PdfFilterType } from '../../types';
import type { DateRange } from '../../../util/types';
import type { TopicChartType } from '../../../mapper/groupTopicReportMapper';
import {
  MathsAge,
  MathsAgeImprovements,
  Progressions,
  QualityLearningTime,
  StudentStatus,
  TopicProgress,
} from '../sections';
import { useLocalStorage } from 'react-use';
import { useI18n } from '@whizz/react-i18n';
import { useIdentity } from '@whizz/react-identity';
import AssessmentInfo from '../modules/AssessmentInfo';
import SensitiveDataToggle from '../modules/SenstiveDataToggle';

type Props = {
  classReport: ClassReportType;
  chartTopicProgress: TopicChartType[];
  filterLabel: string | null;
  filteredIds: number[];
  showStudentStatus: boolean;
  showOthersData: boolean;
  pdfFilter?: PdfFilterType;
  dateRange?: DateRange;
} & Pick<HighlightedProps, 'onElementClick'>;

const ChartsView = (
  {
    classReport,
    chartTopicProgress,
    filterLabel,
    filteredIds,
    showStudentStatus,
    showOthersData,
    pdfFilter,
    dateRange,
    onElementClick,
  }: Props,
): JSX.Element => {
  const { me } = useIdentity();
  const { translate } = useI18n();

  const [ showSensitiveData, setShowSensitiveData ] = useLocalStorage(`class_sensitive_data_${me?.username}`, true);
  const unassessedOrReassessedStudentsIds = classReport.students.unassessedOrReassessedStudents.map((student) => student.id);
  const filteredStudentsNeverAssessed: number[] = filteredIds.filter((r) => unassessedOrReassessedStudentsIds.includes(r));

  const NotAssessedLabelValue = (): string => {
    if (filteredStudentsNeverAssessed.length > 1) {
      return `${(filteredStudentsNeverAssessed.length)} ${translate('global.STUDENT_plural')}`;
    } else {
      return classReport.students.all.filter((student) => student.id === filteredStudentsNeverAssessed[0])[0].fullName;
    }
  };

  const hideAverages = pdfFilter && !pdfFilter.withAverages;
  const hideMathsAge = pdfFilter && !pdfFilter.withMathsAge;
  const showCharts = pdfFilter && pdfFilter.withCharts;

  return (
    <>
      {filteredStudentsNeverAssessed.length > 0 && (
        <AssessmentInfo
          title={translate('pages.REPORTS.CHART_VIEW.not_assessed')}
          highlightCardLabel={NotAssessedLabelValue()}
        />
      )}
      {/* Maths Age */}
      {(!hideMathsAge && showSensitiveData) ?
        <MathsAge
          classReport={classReport}
          showOthersData={showOthersData}
          highlightedLabel={filterLabel}
          highlightedIds={filteredIds}
          hideAverage={hideAverages}
          chartIsExpanded={showCharts}
          onElementClick={onElementClick}
        /> : null
      }
      {/* Maths Age Improvements */}
      <MathsAgeImprovements
        classReport={classReport}
        showOthersData={showOthersData}
        highlightedLabel={filterLabel}
        highlightedIds={filteredIds}
        hideAverage={hideAverages}
        chartIsExpanded={showCharts}
        dateRange={dateRange}
        onElementClick={onElementClick}
      />
      {/* Progressions */}
      <Progressions
        classReport={classReport}
        showOthersData={showOthersData}
        highlightedLabel={filterLabel}
        highlightedIds={filteredIds}
        hideAverage={hideAverages}
        chartIsExpanded={showCharts}
        onElementClick={onElementClick}
      />
      {/* Quality Learning Time */}
      <QualityLearningTime
        classReport={classReport}
        showOthersData={showOthersData}
        highlightedLabel={filterLabel}
        highlightedIds={filteredIds}
        hideAverage={hideAverages}
        chartIsExpanded={showCharts}
        onElementClick={onElementClick}
      />
      {showStudentStatus && (
        <StudentStatus
          classReport={classReport}
          highlightedLabel={null}
          highlightedIds={filteredIds}
          showOthersData={showOthersData}
          chartIsExpanded={showCharts}
          onElementClick={onElementClick}
        />
      )}
      {/* Topic Progress */}
      <TopicProgress
        chartTopicProgress={chartTopicProgress}
        showOthersData={showOthersData}
        showSensitiveData={!hideMathsAge && showSensitiveData}
        highlightedLabel={filterLabel}
        highlightedIds={filteredIds}
      />
      <SensitiveDataToggle
        toggled={!!showSensitiveData}
        toggleHandler={(toggled) => () => setShowSensitiveData(toggled)}
      />
    </>
  );
};

ChartsView.displayName = 'ChartsView';

export default ChartsView;
